import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, TrendingUp, Users, Zap, BarChart3, DollarSign, Briefcase, Rocket, Heart } from 'lucide-react';

const HowToFund = () => {
  const growthStages = [
    {
      icon: <Rocket className="w-12 h-12 text-accent-500" />,
      title: "Startup Stage",
      description: "Seed funding and accelerator programs help businesses launch their ideas.",
    },
    {
      icon: <TrendingUp className="w-12 h-12 text-accent-500" />,
      title: "Growth Stage",
      description: "Working capital loans and venture funding support expanding operations.",
    },
    {
      icon: <BarChart3 className="w-12 h-12 text-accent-500" />,
      title: "Expansion Stage",
      description: "Large investment rounds help businesses scale into new markets.",
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="section bg-gradient-to-br from-primary-50 via-white to-accent-50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-primary-900 leading-tight">
                How to Fund Your{' '}
                <span className="gradient-text">Business</span>
              </h1>
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
                A practical guide to finding the right funding solution for your business.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Every growing business eventually needs additional funding. Whether you are starting a new venture, expanding operations, or managing cash flow, choosing the right financing option can help your business move forward with confidence.
              </p>
              <p className="text-gray-600 leading-relaxed">
                1ST TRUST FINANCES helps businesses explore multiple funding options and connect with lenders who understand their needs.
              </p>
              <Link to="/apply-for-funding" className="btn-primary inline-flex items-center">
                Apply for Funding
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <svg viewBox="0 0 500 400" className="w-full h-auto">
                <defs>
                  <linearGradient id="heroGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#0056d6" />
                    <stop offset="100%" stopColor="#0ea5e9" />
                  </linearGradient>
                </defs>
                <circle cx="250" cy="200" r="150" fill="url(#heroGradient2)" opacity="0.1" />
                <g transform="translate(100, 80)">
                  <circle cx="150" cy="80" r="40" fill="#0056d6" opacity="0.2" />
                  <circle cx="80" cy="120" r="30" fill="#0ea5e9" opacity="0.2" />
                  <circle cx="220" cy="100" r="25" fill="#0056d6" opacity="0.15" />
                  <rect x="50" y="150" width="200" height="120" rx="10" fill="white" stroke="#0056d6" strokeWidth="2" />
                  <rect x="70" y="180" width="100" height="10" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="200" width="140" height="10" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="220" width="80" height="10" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="245" width="160" height="15" rx="5" fill="#0056d6" />
                </g>
              </svg>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="section bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <svg viewBox="0 0 500 400" className="w-full h-auto">
                <defs>
                  <linearGradient id="introGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#0056d6" />
                    <stop offset="100%" stopColor="#0ea5e9" />
                  </linearGradient>
                </defs>
                <circle cx="250" cy="200" r="150" fill="url(#introGradient)" opacity="0.1" />
                <g transform="translate(100, 100)">
                  <rect x="50" y="50" width="150" height="200" rx="10" fill="white" stroke="#0056d6" strokeWidth="2" />
                  <rect x="220" y="100" width="80" height="100" rx="10" fill="#0ea5e9" opacity="0.2" />
                  <circle cx="125" cy="100" r="30" fill="#0056d6" opacity="0.2" />
                  <rect x="70" y="150" width="110" height="10" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="170" width="90" height="10" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="200" width="110" height="30" rx="5" fill="#0056d6" />
                </g>
              </svg>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1 lg:order-2 space-y-6"
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900">
                Access the Right Funding for Your Business
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Businesses often reach a stage where additional capital becomes necessary. This might happen when launching a new product, expanding into new markets, or fulfilling larger customer demand.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Finding the right funding option at the right time can transform a business opportunity into long-term success.
              </p>
              <p className="text-gray-600 leading-relaxed">
                However, with many financing options available today, the process of choosing the right one can feel overwhelming.
              </p>
              <p className="text-gray-600 leading-relaxed">
                1ST TRUST FINANCES simplifies this process by helping businesses understand their funding options and connect with suitable lenders.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Understanding Finance Options */}
      <section className="section bg-gradient-to-b from-primary-50 to-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center space-y-6"
          >
            <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <BarChart3 className="w-10 h-10 text-primary-600" />
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900">
              Understanding Your Finance Options
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              The right funding solution depends on several factors including the stage of your business, the amount of capital required, and your growth plans.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Some funding solutions are better suited for startups, while others work best for established businesses with a strong financial track record.
            </p>
            <p className="text-gray-600 leading-relaxed">
              1ST TRUST FINANCES helps businesses evaluate these factors and identify financing options that align with their goals.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Equity Funding */}
      <section className="section bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900">
                Equity Funding
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Equity finance allows businesses to raise capital by selling shares to investors.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Instead of repaying a loan, the business provides ownership stakes to investors in exchange for funding.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Equity funding is commonly used by high-growth startups that require significant investment to scale their operations.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Investors may also provide mentorship, industry connections, and strategic guidance that can help the business grow faster.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <svg viewBox="0 0 500 400" className="w-full h-auto">
                <defs>
                  <linearGradient id="equityGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#0056d6" />
                    <stop offset="100%" stopColor="#0ea5e9" />
                  </linearGradient>
                </defs>
                <circle cx="250" cy="200" r="150" fill="url(#equityGradient)" opacity="0.1" />
                <g transform="translate(100, 80)">
                  <circle cx="150" cy="100" r="50" fill="#0056d6" opacity="0.2" />
                  <circle cx="80" cy="180" r="35" fill="#0ea5e9" opacity="0.2" />
                  <circle cx="220" cy="180" r="35" fill="#0056d6" opacity="0.15" />
                  <rect x="50" y="240" width="200" height="60" rx="10" fill="white" stroke="#0056d6" strokeWidth="2" />
                  <rect x="70" y="260" width="100" height="20" rx="5" fill="#e6f0ff" />
                </g>
              </svg>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Loan Funding */}
      <section className="section bg-gradient-to-b from-primary-50 to-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <svg viewBox="0 0 500 400" className="w-full h-auto">
                <defs>
                  <linearGradient id="loanGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#0056d6" />
                    <stop offset="100%" stopColor="#0ea5e9" />
                  </linearGradient>
                </defs>
                <circle cx="250" cy="200" r="150" fill="url(#loanGradient)" opacity="0.1" />
                <g transform="translate(100, 100)">
                  <rect x="50" y="50" width="200" height="200" rx="10" fill="white" stroke="#0056d6" strokeWidth="2" />
                  <rect x="70" y="80" width="100" height="15" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="110" width="140" height="15" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="140" width="120" height="15" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="180" width="160" height="50" rx="5" fill="#0056d6" />
                  <DollarSign className="absolute" x="200" y="100" width="40" height="40" fill="#0ea5e9" opacity="0.3" />
                </g>
              </svg>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1 lg:order-2 space-y-6"
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900">
                Business Loan Funding
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Business loans allow companies to access capital while retaining full ownership of their business.
              </p>
              <p className="text-gray-600 leading-relaxed">
                The borrowed amount is repaid over time with interest according to agreed terms.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Today, businesses have access to a wide range of lending solutions including traditional banks and alternative lenders.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Alternative lenders often provide faster decisions and more flexible funding options.
              </p>
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h4 className="font-semibold text-primary-900 mb-4">Types of Business Loans:</h4>
                <ul className="space-y-2">
                  <li className="flex items-center text-gray-600">
                    <ArrowRight className="w-4 h-4 mr-2 text-primary-600" />
                    Unsecured Business Loans
                  </li>
                  <li className="flex items-center text-gray-600">
                    <ArrowRight className="w-4 h-4 mr-2 text-primary-600" />
                    Invoice Financing
                  </li>
                  <li className="flex items-center text-gray-600">
                    <ArrowRight className="w-4 h-4 mr-2 text-primary-600" />
                    Merchant Cash Advance
                  </li>
                  <li className="flex items-center text-gray-600">
                    <ArrowRight className="w-4 h-4 mr-2 text-primary-600" />
                    Business Line of Credit
                  </li>
                  <li className="flex items-center text-gray-600">
                    <ArrowRight className="w-4 h-4 mr-2 text-primary-600" />
                    Peer-to-Peer Lending
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Accelerators */}
      <section className="section bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900">
                Startup Accelerators
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Startup accelerators support early-stage businesses through funding, mentorship, and structured development programs.
              </p>
              <p className="text-gray-600 leading-relaxed">
                These programs typically run for several months and help startups refine their business models, build products, and prepare for investor presentations.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Accelerators provide valuable support including mentorship from experienced entrepreneurs and access to investor networks.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <svg viewBox="0 0 500 400" className="w-full h-auto">
                <defs>
                  <linearGradient id="acceleratorGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#0056d6" />
                    <stop offset="100%" stopColor="#0ea5e9" />
                  </linearGradient>
                </defs>
                <circle cx="250" cy="200" r="150" fill="url(#acceleratorGradient)" opacity="0.1" />
                <g transform="translate(100, 80)">
                  <circle cx="150" cy="100" r="60" fill="#0056d6" opacity="0.2" />
                  <circle cx="80" cy="180" r="40" fill="#0ea5e9" opacity="0.2" />
                  <circle cx="220" cy="180" r="40" fill="#0056d6" opacity="0.15" />
                  <path d="M150 40 L150 160 M90 100 L210 100" stroke="#0056d6" strokeWidth="2" opacity="0.3" />
                </g>
              </svg>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Crowdfunding */}
      <section className="section bg-gradient-to-b from-primary-50 to-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <svg viewBox="0 0 500 400" className="w-full h-auto">
                <defs>
                  <linearGradient id="crowdGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#0056d6" />
                    <stop offset="100%" stopColor="#0ea5e9" />
                  </linearGradient>
                </defs>
                <circle cx="250" cy="200" r="150" fill="url(#crowdGradient)" opacity="0.1" />
                <g transform="translate(100, 100)">
                  <rect x="50" y="50" width="200" height="150" rx="10" fill="white" stroke="#0056d6" strokeWidth="2" />
                  <rect x="70" y="80" width="160" height="80" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="175" width="100" height="15" rx="5" fill="#0056d6" />
                  <circle cx="80" cy="250" r="25" fill="#0ea5e9" opacity="0.2" />
                  <circle cx="150" cy="250" r="25" fill="#0056d6" opacity="0.2" />
                  <circle cx="220" cy="250" r="25" fill="#0ea5e9" opacity="0.15" />
                </g>
              </svg>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1 lg:order-2 space-y-6"
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900">
                Crowdfunding
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Crowdfunding allows businesses to raise money from a large number of people, typically through online platforms.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Instead of relying on a single investor, businesses can present their idea to the public and receive smaller contributions from many supporters.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Crowdfunding can also help validate product ideas and build an early customer community.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Business Growth Lifecycle */}
      <section className="section bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900 mb-4">
              Funding for Every Stage of Business Growth
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Whether you're just starting or looking to scale, we have funding solutions for every stage.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8">
            {growthStages.map((stage, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className="bg-gradient-to-br from-primary-50 to-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="mb-6">{stage.icon}</div>
                <h3 className="font-heading text-xl font-bold text-primary-900 mb-3">
                  {stage.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {stage.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="section gradient-bg text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg viewBox="0 0 1000 1000" className="w-full h-full">
            <defs>
              <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
                <path d="M 50 0 L 0 0 0 50" fill="none" stroke="white" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Find the Right Funding for Your Business
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              1ST TRUST FINANCES helps businesses explore multiple funding solutions and connect with lenders ready to support their growth.
            </p>
            <Link to="/apply-for-funding" className="inline-block bg-white text-primary-600 font-semibold px-8 py-4 rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1">
              Apply for Funding
              <ArrowRight className="inline ml-2 w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HowToFund;
